import * as React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  FormControl,
  CircularProgress,
} from '@mui/material';
import Grid from "@mui/material/Grid2";
import api from '../../../services/api';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { pt } from 'yup-locale-pt';
import { Autocomplete, TextField, IntegerField } from '../../../components';
import { useStoreState, useStoreActions } from 'easy-peasy';
import yupnanable from '../../../services/yup-nanable.js';

yup.setLocale(pt);

const DialogoProducao = ({
  open,
  onClose,
  ano,
  selectedProducao,
  watchEmpresaId
}) => {
  let loading = useStoreState(state => state.nav.loading);
  let startLoading = useStoreActions(actions => actions.nav.startLoading);
  let stopLoading = useStoreActions(actions => actions.nav.stopLoading);
  const [produtos, setProdutos] = React.useState([]);

  const schema = yup
    .object({
      produtoId: yup.number().label('Produto').required(),
      unidades: yup
        .string()
        .min(1, ({ label }) => `${label} deve ter pelo menos 1 item`)
        .label('Unidades')
        .required(),
      producaoBruta: yup
        .number()
        .integer()
        .nullable()
        .nanable()
        .label('Produção Bruta (ton)')
        .required(),
      producaoConcentrado: yup
        .number()
        .integer()
        .nullable()
        .nanable()
        .label('Produção do Concetrado (ton)')
        .required()
    })
    .required();

  const useFormRef = useForm({
    mode: 'onTouched',
    resolver: yupResolver(schema)
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting }
  } = useFormRef;

  const onSave = async data => {
    startLoading();

    try {
      if (data.id) {
        await api.http.put(
          `/00/producao/empresa/${watchEmpresaId}/${data.id}`,
          {
            ...data,
            ano
          }
        );
      } else {
        await api.http.post(`/00/producao/empresa/${watchEmpresaId}`, {
          ...data,
          ano
        });
      }
      stopLoading({ severity: 'success' });
      close(true);
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
      throw error;
    }
  };

  const close = (reload = false) => {
    onClose(reload);
    reset({
      produtoId: '',
      producaoBruta: '',
      producaoConcentrado: '',
      unidades: ''
    });
  };

  const loadProdutos = async mount => {
    startLoading();
    try {
      let data = (await api.http.get('produtos')).data;
      if (mount.isMounted) setProdutos(data);
      stopLoading();
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
      throw error;
    }
  };

  React.useEffect(() => {
    let mount = {
      isMounted: true
    };
    loadProdutos(mount);
    return () => {
      mount.isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    reset(selectedProducao);
  }, [selectedProducao]);

  return (
    (<Dialog open={open} onClose={() => close()}>
      <DialogTitle>Produção</DialogTitle>
      <DialogContent>
        <FormControl sx={{ mb: 2, mt: 2 }} fullWidth>
          <Autocomplete
            field="produtoId"
            useFormRef={useFormRef}
            options={produtos}
            getOptionLabel={produto => produto.name}
            schema={schema}
          />
        </FormControl>
        <DialogContentText>
          Liste as unidades operacionais(plantas) que compõem a sua empresa para
          este produto (um por linha).
        </DialogContentText>
        <FormControl sx={{ my: 2 }} fullWidth>
          <TextField
            margin="dense"
            fullWidth
            field="unidades"
            multiline
            rows={4}
            schema={schema}
            useFormRef={useFormRef}
          />
        </FormControl>
        <Grid size={2}>
          <DialogContentText>
            Produção Bruta "Run of Mine" - ROM - base úmida para este produto
            neste ano de referência:
          </DialogContentText>
          <FormControl sx={{ my: 1 }} fullWidth>
            <IntegerField
              field="producaoBruta"
              schema={schema}
              useFormRef={useFormRef}
            />
          </FormControl>
        </Grid>
        <Grid size={2}>
          <DialogContentText>
            Produção do concentrado para este produto neste ano de referência:
          </DialogContentText>
          <FormControl sx={{ my: 1 }} fullWidth>
            <IntegerField
              field="producaoConcentrado"
              allowNegative={true}
              schema={schema}
              useFormRef={useFormRef}
            />
          </FormControl>
        </Grid>
      </DialogContent>
      <DialogActions>
        {loading > 0 && (
          <CircularProgress
            size={30}
            sx={{ position: 'absolute', left: 25, bottom: 12 }}
          />
        )}
        <Button color="inherit" onClick={() => close()}>
          CANCELAR
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit(onSave)}
          disabled={!isDirty || isSubmitting}
        >
          SALVAR
        </Button>
      </DialogActions>
    </Dialog>)
  );
};

export default DialogoProducao;
